import React, { useState, useEffect } from "react";
import { RiLoginBoxLine, RiLogoutBoxLine } from "react-icons/ri";
import { PiNotepad } from "react-icons/pi";
import { MdOutlineAccessTime } from "react-icons/md";
import { LuCoffee } from "react-icons/lu";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import CustomModel from "../../components/common/CustomModel";
import axios from "axios";
import { googleMapsApiKey } from "../../config/clientsConfig";

const mapLoaderConfig = {
  id: "google-map-script",
  googleMapsApiKey,
  libraries: ["geometry", "places"],
};

const UserTodayActivity = ({ employeeData, employeeStatus }) => {
  const [isNoteVisible, setIsNoteVisible] = useState(false);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);
  const [selectedType, setSelectedType] = useState(""); // "checkIn" or "checkOut"
  const [checkInAddress, setCheckInAddress] = useState("Fetching...");
  const [checkOutAddress, setCheckOutAddress] = useState("Fetching...");
  const [currentLocationAddress, setCurrentLocationAddress] = useState("Loading...");
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const { isLoaded } = useJsApiLoader(mapLoaderConfig);

  // Function to fetch address from coordinates using Nominatim API
  const fetchAddress = async (coordinates, type) => {
    if (!coordinates || coordinates === "") {
      return type === "checkIn" ? setCheckInAddress("No location data") : setCheckOutAddress("No location data");
    }

    setAddressLoading(true);
    try {
      const [lat, lon] = coordinates.split(",");
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`
      );
      const address = response.data?.display_name || "Address not found";
      if (type === "checkIn") {
        setCheckInAddress(address);
      } else {
        setCheckOutAddress(address);
      }
    } catch (error) {
      console.error(`Error fetching ${type} address:`, error);
      const errorMsg = "Failed to fetch address";
      if (type === "checkIn") {
        setCheckInAddress(errorMsg);
      } else {
        setCheckOutAddress(errorMsg);
      }
      setAddressError(errorMsg);
    } finally {
      setAddressLoading(false);
    }
  };

  // Function to fetch address for modal display
  const fetchCurrentLocationAddress = async (coordinates) => {
    if (!coordinates) {
      setCurrentLocationAddress("No location data");
      return;
    }

    try {
      const [lat, lon] = coordinates.split(",");
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`
      );
      const address = response.data?.display_name || "Address not found";
      setCurrentLocationAddress(address);
    } catch (error) {
      console.error("Error fetching current location address:", error);
      setCurrentLocationAddress("Failed to fetch address");
    }
  };

  // Fetch addresses when component mounts or employeeData changes
  useEffect(() => {
    if (employeeData?.loginLocation?.coordinates) {
      fetchAddress(employeeData.loginLocation.coordinates, "checkIn");
    } else {
      setCheckInAddress("No location data");
    }

    if (employeeData?.logoutLocation?.coordinates) {
      fetchAddress(employeeData.logoutLocation.coordinates, "checkOut");
    } else {
      setCheckOutAddress("No location data");
    }
  }, [employeeData]);

  // Handle image modal
  const handleViewImage = (imageUrl, coordinates, type) => {
    setSelectedImage(imageUrl || "");
    setSelectedCoordinates(coordinates || null);
    setSelectedType(type || "");
    setIsImageModalVisible(true);
    if (coordinates) {
      fetchCurrentLocationAddress(coordinates);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow border border-gray-200 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <MdOutlineAccessTime className="text-blue-500 text-2xl" />
          <h2 className="text-lg font-semibold text-gray-800">Today's Activity</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-blue-600 bg-blue-50 text-[12px] border border-blue-500 px-2 py-[2px] rounded-md">
            {employeeStatus || "Unknown"}
          </div>
          <button
            onClick={() => setIsNoteVisible(true)}
            className="flex items-center gap-2 bg-white border px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-orange-50 hover:border-orange-300 hover:shadow-md transition-all duration-200 transform hover:scale-105"
          >
            <PiNotepad className="text-orange-600 text-lg" /> View Notes
          </button>
        </div>
      </div>

      {/* Horizontal scroll container */}
      <div className="flex w-full overflow-x-auto gap-4 scrollbar-hide py-4 px-3">
        {/* Check In */}
        <div className="min-w-[260px] flex justify-between items-center bg-[#F8FAFC] rounded-lg p-4 shadow-sm hover:bg-white border border-transparent hover:border-blue-200 transition-all duration-300 transform hover:scale-[1.02] cursor-pointer">
          <div className="flex items-center gap-3">
            <span className="text-blue-600 border border-blue-500 bg-white rounded-full p-2 flex items-center transition-all duration-200 hover:bg-blue-50 hover:scale-110">
              <RiLoginBoxLine className="text-xl" />
            </span>
            <div>
              <p className="text-sm font-medium text-gray-800 transition-colors duration-200">Check In</p>
              <p className="text-xs text-gray-500 transition-colors duration-200">On Time</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-[12px] font-semibold text-[#717182] transition-colors duration-200">
              {employeeData?.loginTime || "Not Checked In"}
            </span>
            <button
              onClick={() =>
                handleViewImage(
                  employeeData?.loginLocation?.imageUrl,
                  employeeData?.loginLocation?.coordinates,
                  "checkIn"
                )
              }
              className="text-[11px] rounded-full bg-blue-100 border text-blue-600 border-blue-600 px-2 py-[2px] hover:bg-blue-200 hover:shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!employeeData?.loginLocation?.imageUrl}
            >
              View Image
            </button>
          </div>
        </div>

        {/* Check Out */}
        <div className="min-w-[260px] flex justify-between items-center bg-[#F8FAFC] rounded-lg p-4 shadow-sm hover:bg-white border border-transparent hover:border-red-200 transition-all duration-300 transform hover:scale-[1.02] cursor-pointer">
          <div className="flex items-center gap-3">
            <span className="text-red-600 border border-red-500 bg-white rounded-full p-2 flex items-center transition-all duration-200 hover:bg-red-50 hover:scale-110">
              <RiLogoutBoxLine className="text-xl" />
            </span>
            <div>
              <p className="text-sm font-medium text-gray-800 transition-colors duration-200">Check Out</p>
              <p className="text-xs text-gray-500 transition-colors duration-200">On Time</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-[11px] font-semibold text-[#717182] transition-colors duration-200">
              {employeeData?.logoutTime || "Not Checked Out"}
            </span>
            <button
              onClick={() =>
                handleViewImage(
                  employeeData?.logoutLocation?.imageUrl,
                  employeeData?.logoutLocation?.coordinates,
                  "checkOut"
                )
              }
              className="text-[11px] rounded-full bg-red-100 border text-red-600 border-red-600 px-2 py-[2px] hover:bg-red-200 hover:shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!employeeData?.logoutLocation?.imageUrl}
            >
              View Image
            </button>
          </div>
        </div>

        {/* Break Times */}
        {employeeData?.breakTime?.length > 0 ? (
          employeeData.breakTime.map((breakItem, index) => (
            <div
              key={index}
              className="min-w-[260px] flex justify-between items-center bg-[#F8FAFC] rounded-lg p-4 shadow-sm hover:bg-white border border-transparent hover:border-orange-200 transition-all duration-300 transform hover:scale-[1.02] cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <span className="text-orange-500 border border-orange-500 bg-white rounded-full p-2 flex items-center transition-all duration-200 hover:bg-orange-50 hover:scale-110">
                  <LuCoffee className="text-xl" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-800 transition-colors duration-200">
                    Break {index + 1}
                  </p>
                  <span className="text-[12px] font-semibold text-[#717182] transition-colors duration-200">
                    {breakItem}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="min-w-[260px] flex justify-between items-center bg-[#F8FAFC] rounded-lg p-4 shadow-sm hover:bg-white border border-transparent hover:border-orange-200 transition-all duration-300 transform hover:scale-[1.02] cursor-pointer">
            <div className="flex items-center gap-3">
              <span className="text-orange-500 border border-orange-500 bg-white rounded-full p-2 flex items-center transition-all duration-200 hover:bg-orange-50 hover:scale-110">
                <LuCoffee className="text-xl" />
              </span>
              <div>
                <p className="text-sm font-medium text-gray-800 transition-colors duration-200">No Break</p>
                <span className="text-[12px] font-semibold text-[#717182] transition-colors duration-200">
                  No Record
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notes Modal */}
      <CustomModel
        isOpen={isNoteVisible}
        isClose={() => setIsNoteVisible(false)}
        title="Logs Notes"
        content={
          <div className="p-4">
            <textarea
              value={employeeData?.notes?.content || ""}
              className="w-full h-40 p-3 border border-gray-300 rounded-lg text-sm "
              placeholder="Enter your notes here..."
              disabled
            ></textarea>
          </div>
        }
      />

      {/* Enhanced Image & Map Modal */}
      <CustomModel
        isOpen={isImageModalVisible}
        width="w-[950px]"
        isClose={() => {
          setIsImageModalVisible(false);
          setSelectedImage("");
          setSelectedCoordinates(null);
          setCurrentLocationAddress("Loading...");
          setImageLoading(false);
        }}
        title={`${selectedType === "checkIn" ? "Check In" : "Check Out"} Location Details`}
        content={
          <div className="p-">
            {selectedImage ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                

                {/*Left  Side - Map */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-lg font-semibold text-gray-800"> Map View</h3>
                  <div className="w-full h-[400px] rounded-lg overflow-hidden border border-gray-300 shadow-sm">
                    {!isLoaded ? (
                      <div className="flex items-center justify-center h-full bg-gray-100">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="text-sm text-gray-600">Loading map...</span>
                        </div>
                      </div>
                    ) : selectedCoordinates ? (
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "100%" }}
                        center={{
                          lat: parseFloat(selectedCoordinates.split(",")[0]),
                          lng: parseFloat(selectedCoordinates.split(",")[1]),
                        }}
                        zoom={16}
                        options={{
                          styles: [
                            {
                              featureType: "poi",
                              elementType: "labels",
                              stylers: [{ visibility: "off" }]
                            }
                          ]
                        }}
                      >
                        <Marker
                          position={{
                            lat: parseFloat(selectedCoordinates.split(",")[0]),
                            lng: parseFloat(selectedCoordinates.split(",")[1]),
                          }}
                          icon={{
                            url: selectedType === "checkIn" 
                              ? "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                                  <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16 0C7.163 0 0 7.163 0 16c0 16 16 24 16 24s16-8 16-24C32 7.163 24.837 0 16 0z" fill="#2563eb"/>
                                    <circle cx="16" cy="16" r="8" fill="#ffffff"/>
                                    <text x="16" y="21" text-anchor="middle" font-family="Arial" font-size="10" font-weight="bold" fill="#2563eb">IN</text>
                                  </svg>
                                `)
                              : "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                                  <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16 0C7.163 0 0 7.163 0 16c0 16 16 24 16 24s16-8 16-24C32 7.163 24.837 0 16 0z" fill="#dc2626"/>
                                    <circle cx="16" cy="16" r="8" fill="#ffffff"/>
                                    <text x="16" y="21" text-anchor="middle" font-family="Arial" font-size="9" font-weight="bold" fill="#dc2626">OUT</text>
                                  </svg>
                                `),
                            scaledSize: new window.google.maps.Size(32, 40),
                            anchor: new window.google.maps.Point(16, 40),
                          }}
                        />
                      </GoogleMap>
                    ) : (
                      <div className="flex items-center justify-center h-full bg-gray-100">
                        <p className="text-gray-500">No location data available</p>
                      </div>
                    )}
                  </div>
                  
                    {/* Location Info */}
                    <div className="bg-gray-50 p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-700 mb-2">📍 Address Information</h4>
                    <div className="text-sm text-gray-600">
                      {currentLocationAddress === "Loading..." ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span>Loading address...</span>
                        </div>
                      ) : (
                        <p>{currentLocationAddress}</p>
                      )}
                    </div>
                    {selectedCoordinates && (
                      <div className="mt-2 text-xs text-gray-500">
                        <span className="font-medium">Coordinates:</span> {selectedCoordinates}
                      </div>
                    )}
                  </div>
                </div>
                {/* Right Side - Image */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-lg font-semibold text-gray-800">Location Image</h3>
                  <div className="relative">
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg z-10">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="text-sm text-gray-600">Loading image...</span>
                        </div>
                      </div>
                    )}
                    <img
                      src={selectedImage}
                      alt="Location"
                      className="w-full h-[550px] object-cover rounded-lg border border-gray-300 shadow-sm"
                      onLoad={() => setImageLoading(false)}
                      onLoadStart={() => setImageLoading(true)}
                      onError={() => setImageLoading(false)}
                    />
                  </div>
                  
               
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
               
                <p className="text-gray-500 text-lg">No image available</p>
                <p className="text-gray-400 text-sm mt-2">No location image was captured for this activity</p>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default UserTodayActivity;